/**
 * The two calls that go through the worker rather than straight to Supabase,
 * because they need API tokens the browser must never see.
 */

import type { Details } from '../details/types.js';
import type { Snapshot } from '../poller/types.js';
import type { SearchResult } from '../search/types.js';
import { supabase } from './supabase.ts';

async function authHeader(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

export interface SearchResponse {
  results: SearchResult[];
  /** Which catalogues failed, if any. Half a list beats an error message. */
  degraded?: string[];
}

export async function searchCatalogues(
  query: string,
  signal?: AbortSignal,
): Promise<SearchResponse> {
  const res = await fetch(`/api/search?q=${encodeURIComponent(query)}`, {
    headers: await authHeader(),
    ...(signal ? { signal } : {}),
  });
  if (!res.ok) throw new Error(`Search failed (${res.status})`);
  return (await res.json()) as SearchResponse;
}

/** What you chose in the options panel, if you opened it at all. */
export interface AddOptions {
  formats?: readonly string[];
  region?: string | null;
}

export async function addToWatchlist(
  result: SearchResult,
  options: AddOptions = {},
): Promise<string> {
  const res = await fetch('/api/watchlist', {
    method: 'POST',
    headers: { ...(await authHeader()), 'content-type': 'application/json' },
    body: JSON.stringify({
      source: result.source,
      sourceId: result.sourceId,
      kind: result.kind,
      medium: result.medium,
      title: result.title,
      byline: result.byline,
      imageUrl: result.imageUrl,
      tags: result.tags,
      formats: options.formats ?? [],
      region: options.region ?? null,
    }),
  });

  const body = (await res.json().catch(() => ({}))) as { watchableId?: string; error?: string };
  if (!res.ok) throw new Error(body.error ?? `Could not add that (${res.status})`);
  return String(body.watchableId);
}

/**
 * "Go and look again." Covers, blurbs, editions and dates in one press.
 *
 * Returns whether anything was actually fetched: the server declines when it
 * looked moments ago, and saying so is better than a spinner that implies work.
 */
export async function refreshWatchable(
  watchableId: string,
): Promise<{ refreshed: boolean; reason?: string }> {
  const res = await fetch('/api/refresh', {
    method: 'POST',
    headers: { ...(await authHeader()), 'content-type': 'application/json' },
    body: JSON.stringify({ watchableId }),
  });
  const body = (await res.json().catch(() => ({}))) as {
    refreshed?: boolean;
    reason?: string;
    error?: string;
  };
  if (!res.ok) throw new Error(body.error ?? `Could not refresh (${res.status})`);
  return {
    refreshed: Boolean(body.refreshed),
    ...(body.reason ? { reason: body.reason } : {}),
  };
}

/** What the worker knows about an author's site right now. */
export interface SiteState {
  url: string;
  mode: 'feed' | 'page';
  hasFeed: boolean;
  /** When the site last differed from what we had. Null until it ever does. */
  changedAt: string | null;
  latestTitle: string | null;
  checked: boolean;
  unreachable?: boolean;
}

/**
 * Look at the author's site, or set the address to look at.
 *
 * Goes through the worker rather than the browser because an arbitrary site is
 * not CORS-open — Wikipedia is the exception, not the rule. Passing `url`
 * saves it and looks immediately; passing nothing just looks, and the worker
 * declines if it looked within the hour.
 */
export async function checkAuthorSite(
  watchableId: string,
  url?: string | null,
): Promise<SiteState | null> {
  const res = await fetch('/api/site', {
    method: 'POST',
    headers: { ...(await authHeader()), 'content-type': 'application/json' },
    body: JSON.stringify({ watchableId, ...(url === undefined ? {} : { url }) }),
  });
  const body = (await res.json().catch(() => ({}))) as { site?: SiteState | null; error?: string };
  if (!res.ok) throw new Error(body.error ?? `Could not check that (${res.status})`);
  return body.site ?? null;
}

export interface Episode {
  number: number;
  name: string;
  date?: string;
  runtimeMinutes?: number;
  overview?: string;
}

export async function loadSeason(sourceId: string, season: number): Promise<Episode[]> {
  const res = await fetch(`/api/season?id=${encodeURIComponent(sourceId)}&season=${season}`, {
    headers: await authHeader(),
  });
  const body = (await res.json().catch(() => ({}))) as { episodes?: Episode[]; error?: string };
  if (!res.ok) throw new Error(body.error ?? `Could not load that season (${res.status})`);
  return body.episodes ?? [];
}

export interface PeekResponse {
  snapshot: Snapshot | null;
  details: Details | null;
}

/**
 * Cached for the session, because opening the same row twice should not spend
 * two more calls on somebody else's API. There is nowhere to cache it on the
 * far side — the whole point is that no row exists yet.
 */
const peeked = new Map<string, PeekResponse>();

export async function peekCatalogue(
  result: SearchResult,
  signal?: AbortSignal,
): Promise<PeekResponse> {
  const key = `${result.source}:${result.kind}:${result.sourceId}`;
  const hit = peeked.get(key);
  if (hit) return hit;

  const query = new URLSearchParams({
    source: result.source,
    sourceId: result.sourceId,
    kind: result.kind,
    medium: result.medium,
  });

  const res = await fetch(`/api/peek?${query}`, {
    headers: await authHeader(),
    ...(signal ? { signal } : {}),
  });
  if (!res.ok) throw new Error(`Could not look that up (${res.status})`);

  const body = (await res.json()) as PeekResponse;
  // Only a complete answer is cached. The two halves fail independently on
  // the worker, which reports a half or total failure the same way a success
  // is reported — HTTP 200 — so there is nothing in the response shape that
  // says "try again"; only a null tells us. A null half might not be null a
  // minute later, and there is no refresh button on a peek to retry it with,
  // so an incomplete answer must not survive to the next open of the row.
  if (body.snapshot && body.details) peeked.set(key, body);
  return body;
}

export type { SearchResult };
